import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { LoginService } from '@services/login/login.service';
import { PaginatorService } from '@services/paginator/paginator.service';

@Injectable()
export class AuthGuardService {
  private loginService = inject(LoginService);
  private router = inject(Router);
  private paginatorService = inject(PaginatorService);

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> | Promise<boolean | UrlTree> {
    throw new Error('Metodo no implementado');
  }

  /**
   * El access token dura 24hs y el refresh 7 dias (JwtOptions del backend), pero antes este
   * guard solo miraba el access: al volver a entrar al dia siguiente lo veia vencido, hacia
   * logout() — que ademas borraba el refresh token — y mandaba al login. En el celular, donde
   * la app se abre y se cierra todo el tiempo, eso se sentia como "siempre me pide autenticarme".
   *
   * Ahora, si el access esta vencido pero hay refresh token guardado, se intenta renovar la
   * sesion antes de decidir. Como el backend emite un refresh nuevo en cada renovacion, un
   * usuario que entra seguido no vuelve a ver la pantalla de login.
   */
  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree | Observable<boolean | UrlTree> {
    if (this.loginService.isAuthenticated()) {
      // Auditoría QA (2026-08-05): cargarAsync() ya no se dispara desde AppComponent para
      // rutas públicas (ver app.component.ts) — se mueve acá para que el parámetro
      // ItemsPorPagina se cargue apenas el usuario entra a la primera ruta protegida,
      // tanto en un refresh de página ya logueado como en una navegación SPA recién logueado
      // (cargarAsync() es idempotente, solo pega al backend la primera vez).
      this.paginatorService.cargarAsync();
      return true;
    }

    const refreshToken = this.loginService.getRefreshToken();

    if (!refreshToken) {
      return this.irAlLogin(state.url);
    }

    return this.loginService.refreshToken(refreshToken).pipe(
      map((tokenModel) => {
        this.loginService.setToken(tokenModel);
        this.paginatorService.cargarAsync();
        return true;
      }),
      catchError(() => {
        // El refresh también venció o fue revocado: recién ahí se cierra la sesión.
        return of(this.irAlLogin(state.url));
      })
    );
  }

  private irAlLogin(returnUrl: string): UrlTree {
    this.loginService.limpiarSesion();
    return this.router.createUrlTree(['/login'], { queryParams: { returnUrl } });
  }
}
